import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTaskComments, useCreateCommentWithAttachments, useDeleteComment, useDeleteAttachment } from '@hooks';
import { Button, Avatar } from '@components/ui';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000/api';
// Base URL for static files (without /api)
const STATIC_URL = API_URL.replace('/api', '');

function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function formatDate(dateString) {
  const date = new Date(dateString);
  return date.toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getFileIcon(mimeType) {
  if (mimeType.startsWith('image/')) return '🖼️';
  if (mimeType.includes('pdf')) return '📄';
  if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
  if (mimeType.includes('excel') || mimeType.includes('sheet')) return '📊';
  if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return '📽️';
  if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('7z')) return '📦';
  return '📎';
}

function AttachmentPreview({ attachment, onDelete, canDelete }) {
  const isImage = attachment.fileType.startsWith('image/');
  const fileUrl = `${STATIC_URL}${attachment.fileUrl}`;

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 10px',
        backgroundColor: '#f3f4f6',
        borderRadius: '6px',
        border: '1px solid #e5e7eb',
        maxWidth: '200px',
      }}
    >
      {isImage ? (
        <a href={fileUrl} target="_blank" rel="noopener noreferrer" style={{ flexShrink: 0 }}>
          <img
            src={fileUrl}
            alt={attachment.filename}
            style={{
              width: '28px',
              height: '28px',
              objectFit: 'cover',
              borderRadius: '4px',
            }}
          />
        </a>
      ) : (
        <span style={{ fontSize: '16px', flexShrink: 0 }}>{getFileIcon(attachment.fileType)}</span>
      )}
      <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        <a
          href={fileUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'block',
            fontSize: '11px',
            color: '#6366f1',
            fontWeight: '500',
            textDecoration: 'none',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={attachment.filename}
        >
          {attachment.filename}
        </a>
        <span style={{ fontSize: '9px', color: '#9ca3af' }}>
          {formatFileSize(attachment.fileSize)}
        </span>
      </div>
      {canDelete && (
        <button
          type="button"
          onClick={() => onDelete(attachment.id)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#9ca3af',
            padding: '2px',
            fontSize: '12px',
            lineHeight: 1,
            flexShrink: 0,
          }}
          title="Eliminar archivo"
          onMouseEnter={(e) => { e.target.style.color = '#ef4444'; }}
          onMouseLeave={(e) => { e.target.style.color = '#9ca3af'; }}
        >
          ×
        </button>
      )}
    </div>
  );
}

AttachmentPreview.propTypes = {
  attachment: PropTypes.object.isRequired,
  onDelete: PropTypes.func,
  canDelete: PropTypes.bool,
};

function CommentItem({ comment, currentUserId, onDelete, onDeleteAttachment }) {
  const isOwner = comment.userId === currentUserId;

  return (
    <div
      style={{
        display: 'flex',
        gap: '10px',
        padding: '10px 12px',
        backgroundColor: comment.isInternal ? '#fffbeb' : '#ffffff',
        borderRadius: '10px',
        border: comment.isInternal ? '1px solid #fde68a' : '1px solid #f3f4f6',
        transition: 'all 0.15s',
      }}
    >
      <div style={{
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: '12px',
        fontWeight: '600',
        flexShrink: 0,
      }}>
        {comment.user?.name?.charAt(0)?.toUpperCase() || '?'}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '13px', fontWeight: '600', color: '#1f2937' }}>
            {comment.user?.name || 'Usuario'}
          </span>
          <span style={{ fontSize: '10px', color: '#9ca3af' }}>
            {formatDate(comment.createdAt)}
          </span>
          {comment.isInternal && (
            <span
              style={{
                fontSize: '9px',
                padding: '2px 6px',
                borderRadius: '4px',
                backgroundColor: '#fef3c7',
                color: '#b45309',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.03em',
              }}
            >
              Interno
            </span>
          )}
          {isOwner && (
            <button
              type="button"
              onClick={() => onDelete(comment.id)}
              style={{
                marginLeft: 'auto',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#d1d5db',
                fontSize: '11px',
                padding: '2px 6px',
                borderRadius: '4px',
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => {
                e.target.style.color = '#ef4444';
                e.target.style.backgroundColor = '#fee2e2';
              }}
              onMouseLeave={(e) => {
                e.target.style.color = '#d1d5db';
                e.target.style.backgroundColor = 'transparent';
              }}
              title="Eliminar comentario"
            >
              Eliminar
            </button>
          )}
        </div>
        <p
          style={{
            fontSize: '13px',
            color: '#4b5563',
            margin: 0,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            lineHeight: '1.5',
          }}
        >
          {comment.content}
        </p>
        {comment.attachments && comment.attachments.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
            {comment.attachments.map((attachment) => (
              <AttachmentPreview
                key={attachment.id}
                attachment={attachment}
                onDelete={onDeleteAttachment}
                canDelete={isOwner}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

CommentItem.propTypes = {
  comment: PropTypes.object.isRequired,
  currentUserId: PropTypes.number,
  onDelete: PropTypes.func.isRequired,
  onDeleteAttachment: PropTypes.func.isRequired,
};

export default function CommentSection({ taskId, currentUserId }) {
  const [content, setContent] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);

  const { data: commentsData, isLoading } = useTaskComments(taskId, {}, { enabled: !!taskId });
  const createComment = useCreateCommentWithAttachments();
  const deleteComment = useDeleteComment();
  const deleteAttachment = useDeleteAttachment();

  const comments = commentsData?.data || [];

  const handleSubmit = async () => {
    if (!content.trim() && files.length === 0) return;

    try {
      await createComment.mutateAsync({
        taskId,
        content: content.trim(),
        isInternal,
        files,
      });
      setContent('');
      setFiles([]);
      setIsInternal(false);
    } catch (error) {
      console.error('Error creating comment:', error);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles((prev) => [...prev, ...selectedFiles].slice(0, 5)); // Max 5 files
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDeleteComment = async (commentId) => {
    if (window.confirm('¿Eliminar este comentario?')) {
      try {
        await deleteComment.mutateAsync(commentId);
      } catch (error) {
        console.error('Error deleting comment:', error);
      }
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (window.confirm('¿Eliminar este archivo?')) {
      try {
        await deleteAttachment.mutateAsync(attachmentId);
      } catch (error) {
        console.error('Error deleting attachment:', error);
      }
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {/* Header con contador */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 style={{
          fontSize: '13px',
          fontWeight: '600',
          color: '#374151',
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          Comentarios
          {comments.length > 0 && (
            <span style={{
              fontSize: '11px',
              padding: '2px 8px',
              borderRadius: '10px',
              backgroundColor: '#e0e7ff',
              color: '#4f46e5',
              fontWeight: '500',
            }}>
              {comments.length}
            </span>
          )}
        </h3>
      </div>

      {/* Comments List */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          maxHeight: '320px',
          overflowY: 'auto',
          paddingRight: comments.length > 4 ? '4px' : '0',
        }}
      >
        {isLoading ? (
          <div style={{ padding: '24px', textAlign: 'center' }}>
            <p style={{ fontSize: '12px', color: '#9ca3af', margin: 0 }}>Cargando...</p>
          </div>
        ) : comments.length === 0 ? (
          <div style={{
            padding: '20px',
            textAlign: 'center',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px dashed #e5e7eb',
          }}>
            <p style={{ fontSize: '12px', color: '#9ca3af', margin: 0 }}>
              No hay comentarios todavía
            </p>
          </div>
        ) : (
          comments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              currentUserId={currentUserId}
              onDelete={handleDeleteComment}
              onDeleteAttachment={handleDeleteAttachment}
            />
          ))
        )}
      </div>

      {/* New Comment Input - más compacto */}
      <div style={{
        backgroundColor: '#f9fafb',
        borderRadius: '10px',
        padding: '12px',
        border: '1px solid #e5e7eb',
      }}>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Escribe un comentario..."
          rows={2}
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            fontSize: '13px',
            resize: 'none',
            outline: 'none',
            fontFamily: 'inherit',
            backgroundColor: 'white',
            boxSizing: 'border-box',
          }}
          onFocus={(e) => {
            e.target.style.borderColor = '#6366f1';
            e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#e5e7eb';
            e.target.style.boxShadow = 'none';
          }}
        />

        {/* Selected Files Preview */}
        {files.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
            {files.map((file, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 8px',
                  backgroundColor: '#ecfdf5',
                  borderRadius: '6px',
                  fontSize: '11px',
                  border: '1px solid #a7f3d0',
                }}
              >
                <span>{getFileIcon(file.type)}</span>
                <span style={{ maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {file.name}
                </span>
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#6b7280',
                    padding: '0 2px',
                    fontSize: '12px',
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Actions row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* File Upload Button */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.json,.zip,.rar,.7z"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '5px 10px',
                borderRadius: '6px',
                border: '1px solid #e5e7eb',
                backgroundColor: 'white',
                fontSize: '11px',
                color: '#6b7280',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = '#6366f1';
                e.target.style.color = '#6366f1';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.color = '#6b7280';
              }}
              title="Adjuntar archivos"
            >
              📎 Adjuntar
            </button>

            {/* Internal Comment Toggle */}
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                fontSize: '11px',
                color: isInternal ? '#d97706' : '#6b7280',
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '6px',
                backgroundColor: isInternal ? '#fef3c7' : 'transparent',
                transition: 'all 0.15s',
              }}
            >
              <input
                type="checkbox"
                checked={isInternal}
                onChange={(e) => setIsInternal(e.target.checked)}
                style={{ cursor: 'pointer', accentColor: '#d97706' }}
              />
              Interno
            </label>
          </div>

          <Button
            type="button"
            variant="primary"
            size="sm"
            loading={createComment.isPending}
            disabled={!content.trim() && files.length === 0}
            onClick={handleSubmit}
            style={{ fontSize: '12px', padding: '6px 14px' }}
          >
            Enviar
          </Button>
        </div>
      </div>

      {createComment.isError && (
        <p style={{ fontSize: '11px', color: '#ef4444', margin: 0 }}>
          Error: {createComment.error?.response?.data?.message || 'Error al crear comentario'}
        </p>
      )}
    </div>
  );
}

CommentSection.propTypes = {
  taskId: PropTypes.number.isRequired,
  currentUserId: PropTypes.number,
};
